import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _isNil from 'lodash/isNil';
import _toInteger from 'lodash/toInteger';
import PropTypes from 'prop-types';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Colors
import { statusPalette } from 'components/core/colors';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import { statusIcon, statusFromBool } from 'models/async';
import {
  installationName, networkLevelIcon, networkAccessTechnology, synchronized, commands as deviceCommands,
} from 'models/device';
import { desc as operatorDesc } from 'models/operator';
import { icon as simcardIcon, status as simcardStatus } from 'models/simcard';
// Custom
import { getDeviceSync } from 'actions';
import { listRowSpecsShape, deviceDataShape } from 'constants/PropTypesShapes';
import { showPath } from 'devices/navigation';
import withDeviceData from 'appAssets/hocs/WithDeviceData';
import DeviceStatusWidget from 'devices/assets/main/DeviceStatusWidget';
import DeviceCmdTriggerItem from 'devices/assets/main/DeviceCmdTriggerItem';
import AppIcon from 'components/icons/AppIcon';
import { ShortcutIconButton } from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';

const DeviceAsyncStatus = ({ deviceData }) => {
  const globalStatus = statusFromBool(synchronized(deviceData));
  const { icon, color } = statusIcon(globalStatus);
  return (
    <div className="d-flex align-items-center">
      <Tooltip content={<LocalizedMessage id={`syncStatus.${globalStatus}`} />}>
        <AppIcon id={icon} color={color} />
      </Tooltip>
    </div>
  );
};

DeviceAsyncStatus.propTypes = {
  deviceData: deviceDataShape.isRequired,
};

const DeviceNetwork = ({ deviceData }) => {
  const { info } = deviceData;

  if (info) {
    const { network_access_technology: networkAccessTechnologyDesc, network_level: networkLevel, network_operator: networkOperator } = info;
    if (!_isNil(networkLevel)) {
      const { icon, color } = networkLevelIcon(networkLevel);
      const { icon: networkAccessTechnologyIcon } = networkAccessTechnology(networkAccessTechnologyDesc) || {};
      return (
        <div className="d-flex align-items-center">
          <Tooltip content={`${networkLevel}/5`}>
            <AppIcon id={icon} color={color} className={classnames({ 'mr-2': networkAccessTechnologyIcon })} />
            {networkAccessTechnologyIcon && (
              <AppIcon id={networkAccessTechnologyIcon} defaultColor fontSize="small" className="mr-2" style={{ marginTop: '-4px' }} />
            )}
          </Tooltip>
          {networkOperator && (
            <TruncatedTooltipText>
              {operatorDesc(networkOperator)}
            </TruncatedTooltipText>
          )}
        </div>
      );
    }
  }
  return null;
};

DeviceNetwork.propTypes = {
  deviceData: deviceDataShape.isRequired,
};

const DeviceSimcard = ({ deviceData }) => {
  const { simcard } = deviceData;
  if (simcard) {
    const { color } = simcardStatus(simcard);

    return (
      <div className="d-flex align-items-center">
        <Tooltip content={<LocalizedMessage id="info.simcardWithStatus" values={{ status: simcard.status }} />}>
          <AppIcon id={simcardIcon} color={color} className="mr-2" />
        </Tooltip>
        <TruncatedTooltipText>
          {simcard.number || <LocalizedMessage id="info.simcardNoNumber" />}
        </TruncatedTooltipText>
      </div>
    );
  }
  return null;
};

DeviceSimcard.propTypes = {
  deviceData: deviceDataShape.isRequired,
};

const DeviceRow = React.forwardRef(({
  id, device, deviceData, rowSpecs, onSync, handleGetDeviceSync,
}, ref) => {
  if (deviceData) {
    const { id: deviceId } = deviceData;

    // Get sync of device on component mount
    useEffect(() => {
      handleGetDeviceSync(deviceId);
    }, []);

    // Installation information
    const { installation: { device: deviceInstallation } } = deviceData;

    // Permissions
    const { pInstallation: { configRead }, pDevice } = permissions;

    // Handlers
    const handleSync = () => {
      onSync(deviceData);
    };

    return (
      <TableRow
        ref={ref}
        element={device}
        rowSpecs={rowSpecs}
        actions={[
          <OnlyWith
            key="bActions"
            permissions={{
              needed: pDevice.read,
              permissionsOrContext: 'userPermissions',
            }}
          >
            <React.Fragment>
              <DeviceCmdTriggerItem itemType="icon" id={deviceId} cmd={deviceCommands.sync} onTrigger={handleSync} />
              <ShortcutIconButton primary path={showPath(deviceId)} />
            </React.Fragment>
          </OnlyWith>,
        ]}
      >
        <TableCell className={classnames({ 'table-first-details': rowSpecs.withDetails })}>
          <div className="d-flex align-items-center">
            {deviceInstallation.disabled && (
              <Tooltip content={<LocalizedMessage id="tooltip.deviceDisabled" />}>
                <AppIcon id="disabled" color={statusPalette.warning} className="mr-2" />
              </Tooltip>
            )}
            <TruncatedTooltipText>{deviceData.serial}</TruncatedTooltipText>
          </div>
        </TableCell>
        <TableCell className="hidden-xs-down">
          <TruncatedTooltipText>{installationName(deviceData)}</TruncatedTooltipText>
        </TableCell>
        <TableCell className="table-content-1" noPadding>
          <DeviceStatusWidget
            device={deviceData}
            source="installation"
            parentId={_toInteger(id)}
            tooltipPermission={{
              needed: configRead,
              permissionsOrContext: 'installationPermissions',
            }}
          />
        </TableCell>
        <TableCell className="table-content-1 table-content-center hidden-xs-down" noPadding>
          <DeviceAsyncStatus deviceData={deviceData} />
        </TableCell>
        <TableCell className="hidden-xs-down">
          <DeviceNetwork deviceData={deviceData} />
        </TableCell>
        <TableCell className="hidden-xs-down">
          <DeviceSimcard deviceData={deviceData} />
        </TableCell>
      </TableRow>
    );
  }
  return null;
});

DeviceRow.displayName = 'DeviceRow';

DeviceRow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  deviceData: deviceDataShape,
  rowSpecs: listRowSpecsShape.isRequired,
  onSync: PropTypes.func.isRequired,
  handleGetDeviceSync: PropTypes.func.isRequired,
};

DeviceRow.defaultProps = {
  deviceData: null,
};

const mapDispatchToProps = (dispatch) => ({
  handleGetDeviceSync: (id) => {
    dispatch(getDeviceSync(id));
  },
});

module.exports = compose(
  connect(
    null,
    mapDispatchToProps,
  ),
  withDeviceData('device.id'),
)(DeviceRow);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/configuration/DeviceRow.js